/**
 * Card Component
 *
 * A flexible container component with elevation levels and padding options.
 * Built with Community Trust design tokens.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Shadow/elevation level
   */
  elevation?: 'sm' | 'md' | 'lg' | 'none';

  /**
   * Internal padding
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * Border radius
   */
  rounded?: 'md' | 'lg' | 'xl';

  /**
   * Whether to show hover effect
   */
  hoverable?: boolean;

  /**
   * Background color
   */
  variant?: 'white' | 'light' | 'transparent';

  /**
   * Card content
   */
  children: React.ReactNode;
}

/**
 * Card component using Community Trust design system
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      elevation = 'md',
      padding = 'md',
      rounded = 'lg',
      hoverable = false,
      variant = 'white',
      className,
      children,
      ...props
    },
    ref
  ) => {
    // Base styles
    const baseStyles = clsx(
      'transition-shadow duration-base', // 200ms from design tokens
      {
        'cursor-pointer hover:shadow-lg': hoverable,
      }
    );

    // Elevation styles (using design token shadows)
    const elevationStyles = {
      none: '',
      sm: 'shadow-sm',
      md: 'shadow-md',
      lg: 'shadow-lg',
    };

    // Padding styles (using design token spacing)
    const paddingStyles = {
      none: '',
      sm: 'p-4',  // 16px
      md: 'p-6',  // 24px
      lg: 'p-8',  // 32px
    };

    // Border radius styles (using design token radii)
    const roundedStyles = {
      md: 'rounded-lg',  // 8px
      lg: 'rounded-xl',  // 12px
      xl: 'rounded-2xl', // 16px
    };

    // Variant styles
    const variantStyles = {
      white: 'bg-white',
      light: 'bg-neutral-50',
      transparent: 'bg-transparent',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          baseStyles,
          elevationStyles[elevation],
          paddingStyles[padding],
          roundedStyles[rounded],
          variantStyles[variant],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header - Optional header section for cards
 */
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('mb-4', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Title - Typography for card titles
 */
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={clsx('text-h2 font-display font-semibold text-neutral-900', className)}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = 'CardTitle';

/**
 * Card Description - Typography for card descriptions
 */
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

export const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={clsx('text-body text-neutral-600', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = 'CardDescription';

/**
 * Card Content - Main content area
 */
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

/**
 * Card Footer - Optional footer section
 */
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('mt-6 pt-4 border-t border-neutral-200', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

export default Card;
